import { useRef } from "react";
import { useIntersectionObserver } from "../../../lib/use-intersection-observer";
import clsx from "clsx";
import styles from "./author-social-share.module.css";
import {
  backgroundColorStyleObject,
  topBottomPaddingStyleObject,
} from "../../../lib/utilities";

export type AuthorSocialShareData = {
  id?: string;
  backgroundColor?: string;
  socialShare?: boolean;
  socialShareLabel?: string;
  authorLine?: string;
  publishedDate?: string;
  reduceWidth?: boolean;
  reduceBottomSpacing?: string;
  reduceTopSpacing?: string;
  socialShareLinks?: {
    socialIcon: string;
    socialIconAlt: string;
    socialShareUrl: string;
  }[];
};

type SocialShareLinksProps = {
  socialShareLabel?: AuthorSocialShareData["socialShareLabel"];
  socialShareLinks?: AuthorSocialShareData["socialShareLinks"];
  publishedDate?: AuthorSocialShareData["publishedDate"];
  authorLine?: AuthorSocialShareData["authorLine"];
  socialShare?: AuthorSocialShareData["socialShare"];
};

type AuthorSocialShareProps = {
  data: AuthorSocialShareData;
  animateIntoView?: boolean;
};

const BottomSection = ({
  socialShareLabel,
  socialShareLinks,
  authorLine,
  publishedDate,
  socialShare,
}: SocialShareLinksProps) => (
  <div className={styles.bottomContainer}>
    <div className={styles.authorContainer}>
      {authorLine && <div>{authorLine}</div>}
      {publishedDate && <div>{publishedDate}</div>}
    </div>
    {socialShare && (
      <div className={styles.socialContainer}>
        {socialShareLabel}{" "}
        {socialShareLinks?.map((social, socialIndex) => (
          <a
            key={socialIndex}
            href={social.socialShareUrl}
            target="_blank"
            rel="noreferrer"
          >
            <img
              className={styles.socialIcon}
              src={social.socialIcon}
              width={25}
              height={25}
              alt={social.socialIconAlt}
            />
          </a>
        ))}
      </div>
    )}
  </div>
);

export const AuthorSocialShare = ({
  data,
  animateIntoView = true,
}: AuthorSocialShareProps) => {
  const {
    id,
    backgroundColor,
    socialShare,
    socialShareLabel,
    socialShareLinks,
    authorLine,
    publishedDate,
    reduceWidth,
    reduceTopSpacing,
    reduceBottomSpacing,
  } = data;
  const ref = useRef<HTMLDivElement | null>(null);
  const viewportEntry = useIntersectionObserver(ref, {
    freezeOnceVisible: true,
  });
  const isVisible = !!viewportEntry?.isIntersecting;
  const reducedPaddingCss = topBottomPaddingStyleObject(
    reduceTopSpacing,
    reduceBottomSpacing,
  );

  const backgroundColorCss = backgroundColorStyleObject(backgroundColor);

  return (
    <section
      ref={ref}
      id={id ?? undefined}
      className={styles.container}
      style={{ ...reducedPaddingCss, ...backgroundColorCss }}
    >
      <div className="defaultLayoutOuter">
        <div
          className={clsx("defaultLayoutInner ", {
            defaultAnimatableIntoView: animateIntoView,
            defaultGrid: reduceWidth,
            defaultPlayAnimationIntoView: animateIntoView && isVisible,
          })}
        >
          <BottomSection
            socialShareLabel={socialShareLabel}
            socialShareLinks={socialShareLinks}
            socialShare={socialShare}
            authorLine={authorLine}
            publishedDate={publishedDate}
          />
        </div>
      </div>
    </section>
  );
};
